import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  CircularProgress,
  Select,
  MenuItem,
} from '@mui/material';
import { toast } from 'react-toastify';
import { UserContext } from '../hooks';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import axiosInstance from '../services/axiosInstance';
import { useNavigate } from 'react-router-dom';

// const stripePromise = loadStripe(
//   process.env.REACT_APP_STRIPE_KEY ||
//     'pk_test_51Sp1NtH64GjOQsXERoU1HBJGme40qv33nXMHgfT4NDSnUf1IGMib5MFcZ2kk7VM1gdt4rQprta6DC2ywsan09Oyt00l5XCyQ38'
// );
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const ConfirmationDialog = ({
  open,
  setOpen,
  mode, // "confirm" o "cancel"
  courtConfirmation,
  bookId,
  cancelTime,
  setCancelTime,
  timeIntervals,
  times,
  setTimes,
  selectedDate,
  setCourtBooked,
}) => {
  const { user, isMember } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [isLeagueMatch, setIsLeagueMatch] = useState(false);
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLeagueMatch && members.length === 0) {
      const fetchData = async () => {
        try {
          const response = await axiosInstance.get('/users/all?league=true');
          if (response.success) {
            setMembers(response.users);
          } else {
            console.error('No se pudo cargar la lista de socios.');
          }
        } catch (error) {
          console.error('Error al cargar socios:', error);
        }
      };

      fetchData();
    }
  }, [isLeagueMatch]);

  const handlePaymentAndConfirm = async () => {
    if (!stripe || !elements) {
      toast.error('Stripe no está listo. Intenta nuevamente.');
      return;
    }

    setLoading(true);
    try {
      // Crear el PaymentIntent
      const response = await axiosInstance.post(
        `/users/booking/createPaymentIntent`,
        {
          courtBooked: courtConfirmation,
          timeBooked: [...times].map(time => timeIntervals[time]),
          dateBooked: new Date(selectedDate).toLocaleDateString('sv-SE'),
          userBooked: user.id,
          players: [
            {
              memberId: user.id,
              isMember: false,
              amount:
                user.role === 'guess'
                  ? courtConfirmation.type === 'padel'
                    ? courtConfirmation.costs.nonMember * 4
                    : courtConfirmation.costs.nonMember * 2
                  : courtConfirmation.type === 'padel'
                    ? courtConfirmation.costs.uca * 4
                    : courtConfirmation.costs.uca * 2,
            },
          ],
          paymentStatus: 'C',
        }
      );
      const { clientSecret } = response;

      // Confirmar el pago con Stripe
      const cardElement = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (result.error) {
        toast.error('Error procesando el pago. Intenta nuevamente.');
      } else if (result.paymentIntent.status === 'succeeded') {
        // PREAUTORIZACIONES DESACTIVADAS: no llamar a confirmPreauthorization ni aceptar requires_capture.
        toast.success('Pago completado exitosamente.');
        setOpen(false);
        setTimeout(() => navigate('/bookings'), 2000);
      }
    } catch (error) {
      console.error('Error procesando el pago:', error);
      // toast.error(
      //   error.response?.data?.message || 'Ocurrió un error al procesar el pago.'
      // );
      toast.error('Ocurrió un error al procesar el pago.');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    let players = [];
    const dayKey = selectedDate.toLocaleDateString('sv-SE'); // Asegurar formato correcto
    setLoading(true);

    if (mode === 'confirm') {
      // Lógica para confirmar la reserva
      try {
        if (isLeagueMatch) {
          if (!selectedMemberId) {
            toast.error('Selecciona un jugador rival.');
            setLoading(false);
            return;
          }
          const rivalMember = members.find(m => m._id === selectedMemberId);
          players = [
            { memberId: user.id, isMember: isMember },
            {
              memberId: rivalMember._id,
              isMember: !['uca', 'guess'].includes(rivalMember.role),
            },
          ];
        } else {
          players = [{ memberId: user.id, isMember: isMember }];
        }
        const response = await axiosInstance.post(`/users/booking`, {
          courtId: courtConfirmation._id,
          timeBooked: [...times].map(time => timeIntervals[time]),
          dateBooked: new Date(selectedDate).toLocaleDateString('sv-SE'),
          userBooked: user.id,
          players,
          isLeagueMatch,
          paymentStatus:
            user.role === 'guess' || user.role === 'uca' ? 'P' : 'C',
        });

        if (response.success) {
          const booking = response.booking;
          setCourtBooked(prev => {
            const updated = new Map(prev); // Clonar correctamente el Map
            if (!updated.has(dayKey)) {
              updated.set(dayKey, new Map());
            }
            updated.get(dayKey).set(booking.timeBooked, booking);
            return updated;
          });
          toast.success(
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>Reserva confirmada</Typography>
              <Button
                size="small"
                color="inherit"
                onClick={() => navigate(`/bookings/${booking._id}`)}
              >
                Ver
              </Button>
            </Box>,
            { autoClose: 5000 }
          );
          setTimes(new Set());
        } else {
          toast.error(response.msg || 'Error al confirmar la reserva.');
        }
      } catch (err) {
        toast.error(
          err.response.data.message || 'Error al confirmar la reserva.'
        );
      } finally {
        setLoading(false);
      }
    } else if (mode === 'cancel') {
      // Lógica para cancelar la reserva
      try {
        const response = await axiosInstance.delete(
          `/users/booking/${bookId}/delete`
        );

        if (response.success) {
          setCourtBooked(prev => {
            const updated = new Map(prev); // Clonar correctamente el Map
            if (updated.has(dayKey)) {
              const bookingsMap = updated.get(dayKey);
              bookingsMap.forEach((bookingObj, time) => {
                if (bookingObj._id === bookId) {
                  bookingsMap.delete(time);
                }
              });

              if (bookingsMap.size === 0) {
                updated.delete(dayKey); // Eliminar el día si no quedan reservas
              }
            }
            return updated;
          });
          toast.success('Reserva cancelada exitosamente.');
          setCancelTime(null);
        } else {
          toast.error('Error al cancelar la reserva.');
        }
      } catch (err) {
        console.error(err);
        // toast.error(
        //   err.response?.data?.message || 'Error al cancelar la reserva.'
        // );
        toast.error('Error al cancelar la reserva.');
      } finally {
        setLoading(false);
      }
    }

    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
      {open && ( // 👈 Evita el cambio brusco de color al cerrar
        <DialogTitle
          sx={{
            backgroundColor: mode === 'confirm' ? 'primary.main' : 'error.main',
            color: 'primary.contrastText',
            fontWeight: 'bold',
            textAlign: 'center',
            py: 2,
          }}
        >
          {mode === 'confirm'
            ? 'Confirmación de Reserva'
            : 'Confirmación de Cancelación'}
        </DialogTitle>
      )}
      <Divider />
      <DialogContent sx={{ py: 3 }}>
        {mode === 'confirm' && !isMember ? (
          <>
            {/* Información de la reserva */}
            <Box sx={{ mb: 2 }}>
              <Typography>
                <strong>
                  {courtConfirmation.type.toUpperCase()}{' '}
                  {courtConfirmation.name}
                </strong>
              </Typography>
              <Typography>
                <strong>Fecha:</strong>{' '}
                {selectedDate.toLocaleDateString('es-ES', {
                  weekday: 'long', // Nombre completo del día
                  day: 'numeric', // Día del mes
                  month: 'long', // Nombre completo del mes
                  year: 'numeric', // Año completo
                })}
              </Typography>
              <Typography>
                <strong>Hora(s):</strong>{' '}
                {times &&
                  [...times]
                    .sort((a, b) => a - b)
                    .map(time => timeIntervals[time])
                    .join(', ')}
              </Typography>
              <Typography color="primary" sx={{ mt: 1 }}>
                <strong>Total a pagar:</strong>{' '}
                {user.role === 'guess'
                  ? courtConfirmation.type === 'padel'
                    ? courtConfirmation.costs.nonMember * 4
                    : courtConfirmation.costs.nonMember * 2
                  : courtConfirmation.type === 'padel'
                    ? courtConfirmation.costs.uca * 4
                    : courtConfirmation.costs.uca * 2}{' '}
                €
              </Typography>
            </Box>

            {/* Mensaje de pago */}
            <Typography>
              {/* Como invitado, debes introducir la tarjeta para confirmar la
              reserva. Si faltan más de 24 horas, el importe quedará solo pre
              autorizado y se capturará automáticamente dentro de las 24 horas
              previas. */}
              Como invitado, debes pagar la totalidad de la pista para confirmar
              tu reserva.
            </Typography>

            {/* Formulario de tarjeta de Stripe */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            >
              <CardElement options={{ hidePostalCode: true }} />
            </Box>

            {/* Pregunta de liga solo si está en una liga y no es padel */}
            {user.league && courtConfirmation?.type !== 'padel' && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isLeagueMatch}
                      onChange={e => setIsLeagueMatch(e.target.checked)}
                    />
                  }
                  label="¿Es un partido de liga?"
                />

                {isLeagueMatch && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selecciona el jugador rival
                    </Typography>
                    <Select
                      value={selectedMemberId}
                      onChange={e => setSelectedMemberId(e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">-- Selecciona un jugador --</MenuItem>
                      {members.map(member => (
                        <MenuItem key={member._id} value={member._id}>
                          {`${member.name || ''} ${member.surname || ''}`.trim()}{' '}
                          ({member.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Confirmación estándar para otros usuarios */}
            <Typography textAlign="center">
              {mode === 'confirm'
                ? `¿Confirmar reserva de ${courtConfirmation.type.toUpperCase()} ${courtConfirmation.name} de ${[
                    ...times,
                  ]
                    .sort((a, b) => a - b)
                    .map(time => timeIntervals[time])
                    .join(', ')} el ${selectedDate.toLocaleDateString('es-ES', {
                    weekday: 'long', // Nombre completo del día
                    day: 'numeric', // Día del mes
                    month: 'long', // Nombre completo del mes
                    year: 'numeric', // Año completo
                  })}?`
                : `¿Estás seguro que deseas cancelar tu reserva de ${courtConfirmation?.name} para ${timeIntervals[cancelTime]} el ${selectedDate.toLocaleDateString(
                    'es-ES',
                    {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    }
                  )}?`}
            </Typography>

            {user.league &&
              courtConfirmation?.type !== 'padel' &&
              mode === 'confirm' && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isLeagueMatch}
                        onChange={e => setIsLeagueMatch(e.target.checked)}
                      />
                    }
                    label="¿Es un partido de liga?"
                  />

                  {isLeagueMatch && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Selecciona el jugador rival
                      </Typography>
                      <Select
                        value={selectedMemberId}
                        onChange={e => setSelectedMemberId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">
                          -- Selecciona un jugador --
                        </MenuItem>
                        {members.map(member => (
                          <MenuItem key={member._id} value={member._id}>
                            {`${member.name || ''} ${member.surname || ''}`.trim()}{' '}
                            ({member.email})
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                </Box>
              )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', py: 2 }}>
        <Button
          onClick={() => setOpen(false)}
          variant="outlined"
          color="primary"
          sx={{ minWidth: 120 }}
        >
          No, Volver
        </Button>
        <Button
          onClick={
            // mode === 'cancel' || isMember || isLeagueMatch
            //   ? handleAction
            //   : handlePaymentAndConfirm
            isMember || isLeagueMatch ? handleAction : handlePaymentAndConfirm
          }
          variant="contained"
          color={mode === 'confirm' ? 'primary' : 'error'}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            `Sí, ${mode === 'confirm' ? 'Confirmar' : 'Cancelar'}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ConfirmationDialogWrapper = props => (
  <Elements stripe={stripePromise}>
    <ConfirmationDialog {...props} />
  </Elements>
);

export default ConfirmationDialogWrapper;
